import { useState, useCallback } from 'react'
import { numberFormat } from 'shared/util'

import { Stack, TableCell, Typography } from '@mui/material'

import { ManufacturerResult } from 'interface/manufacturer'

import { Loading } from 'components/controls/loading'
import { ErrorInfo } from 'components/controls/errorInfo'
import { ValueSpan } from 'components/controls/valueSpan'
import { SearchTableBody } from 'components/controls/searchTable'

import { ConfigureListHeader } from '../components/header'
import { ManufacturerLogo } from 'features/manufacturer/components/logo'
import { AddManufacturerDialog } from 'features/manufacturer/dialog/add'
import { EditManufacturerDialog } from 'features/manufacturer/dialog/edit'

import { useManufacturerManagementContainer } from './container'

export const ConfigureManufacturerPage = () => {
  const {
    loading,
    error,
    manufacturerList,
    searchWord,
    setSearchWord,
		onAddManufacturer,
		onEditManufacturer,
		onDeleteManufacturer,
  } = useManufacturerManagementContainer()

	const [modal, setModal] = useState<{
		mode: "addManufacturer" | "editManufacturer"
		manufacturer?: ManufacturerResult
  } | null>(null)

	const closeModal = useCallback(
		() => setModal(null),
		[setModal]
	)
	const openAddManufacturerDialog = useCallback(
		() => setModal({ mode: "addManufacturer" }),
		[setModal]
	)
	const openEditManufacturerDialog = useCallback(
		(manufacturer: ManufacturerResult) => setModal({ mode: "editManufacturer", manufacturer }),
		[setModal]
	)
  return (
    <>
      {loading && <Loading />}

      <ConfigureListHeader
        openAddButtonLabel="新しい製薬企業を追加"
        openAddDialog={openAddManufacturerDialog}
        searchWord={searchWord}
        setSearchWord={setSearchWord}
      >
        <ValueSpan text={numberFormat(manufacturerList?.length, '')} suffix='社'/>
      </ConfigureListHeader>
      
      <Stack direction="column" spacing={1}>
        {error && <ErrorInfo error={error}/>}
      </Stack>

      <Stack flexGrow={1} overflow="auto">
        {manufacturerList && (
          <SearchTableBody<ManufacturerResult>
            list={manufacturerList}
            columnList={[
              {
                label: '',
                cell: (r) => (
                  <TableCell key={`logo-${r.manufacturerId}`} align="center" sx={{ whiteSpace: 'nowrap' }}>
                    <ManufacturerLogo manufacturer={r}/>
                  </TableCell>
                )
              },
              {
                label: '企業名',
                cell: (r) => (
                  <TableCell key={`name-${r.manufacturerId}`} sx={{ whiteSpace: "nowrap" }}>
                    {r.manufacturerNameEng ? (
                      <Stack direction='column' alignItems="flex-start">
                        <Typography variant="body1">{r.manufacturerName}</Typography>
                        <Typography variant="body2" color="textSecondary">{r.manufacturerNameEng}</Typography>
                      </Stack>
                    ) : (
                      <Typography variant="body1">{r.manufacturerName}</Typography>
                    )}
                  </TableCell>
                )
              },
              { label: '備考', value: (r) => r.description, expand: true }
            ]}
            onClickRow={openEditManufacturerDialog}
          />
        )}
      </Stack>

      {modal?.mode === 'addManufacturer' ? (
        <AddManufacturerDialog
          onAddManufacturer={onAddManufacturer}
          onClose={closeModal}
        />
      ) : (modal?.mode === 'editManufacturer' && modal?.manufacturer) ? (
        <EditManufacturerDialog
          manufacturer={modal.manufacturer}
          onDeleteManufacturer={onDeleteManufacturer}
          onEditManufacturer={onEditManufacturer}
          onClose={closeModal}
        />
      ) : (
        <></>
      )}
    </>
  )
}
